import { useQuery } from "@tanstack/react-query";
import { GitBranch, AlertTriangle } from "lucide-react";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatDateTime } from "@/lib/format";

export function PlacementQueuePage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-placement-queue"],
    queryFn: AdminAPI.placementQueue,
    refetchInterval: 15_000,
  });

  const items = data ?? [];
  const failed = items.filter((c) => c.status === "failed");

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Placement Queue</h1>
        <p className="text-sm text-surface-500 mt-1">
          Live view of candidates awaiting tree placement. Auto-refreshes every 15s.
        </p>
      </header>

      {failed.length > 0 && (
        <Card className="border-danger-200 bg-danger-50/40">
          <CardBody className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-danger-700 shrink-0 mt-0.5" />
            <div className="text-sm text-danger-900">
              <div className="font-semibold">{failed.length} candidate(s) failed placement</div>
              <p className="mt-1">These exceeded MAX_PLACEMENT_RETRIES and require manual admin review.</p>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={GitBranch} title="Queue is empty"
                        description="All entries have been placed successfully." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Queue #</TH><TH>Entry ID</TH><TH>Status</TH>
                  <TH>Retries</TH><TH>Last Error</TH><TH>Enqueued</TH>
                </TR>
              </THead>
              <tbody>
                {items.map((c) => (
                  <TR key={c.id}>
                    <TD className="font-mono">{c.queue_number}</TD>
                    <TD className="font-mono text-xs text-brand-700 font-semibold">{c.entry_id}</TD>
                    <TD><StatusPill status={c.status} /></TD>
                    <TD>{c.retry_count}</TD>
                    <TD className="text-xs text-danger-700 max-w-xs truncate">
                      {c.last_error || "—"}
                    </TD>
                    <TD className="text-2xs text-surface-500">{formatDateTime(c.created_at)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
